/**
 * LinkedInApprovals.jsx — founder-only panel at the top of the Home Task Hub
 * "Social Posts" tab. Three sections:
 *  1. This week's LinkedIn duty roster (planning forecast).
 *  2. Posts awaiting approval, with Approve / Request Edit (and any photo).
 *  3. Recently approved posts, showing which have gone live ("Posted").
 */
import React, { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Linkedin, Check, MessageSquare, Loader2, CheckCircle2, Clock, CalendarDays, BarChart3, Pause, Play, Users, Download, Trophy, Flame } from "lucide-react";

const DOW = (d) => {
  try { return new Date(d + "T00:00:00").toLocaleDateString(undefined, { weekday: "short" }); }
  catch { return d; }
};

const MONTH_LABEL = (m) => {
  try { return new Date(m + "-01T00:00:00").toLocaleDateString(undefined, { month: "short", year: "numeric" }); }
  catch { return m; }
};

const STREAK_TIER = (n) => {
  if (n >= 5) return "border-amber-400 bg-amber-50 text-amber-700";
  if (n >= 3) return "border-orange-300 bg-orange-50 text-orange-700";
  return "border-slate-300 bg-slate-50 text-slate-600";
};

export default function LinkedInApprovals() {
  const [items, setItems] = useState(null);
  const [todayAssignee, setTodayAssignee] = useState(null);
  const [roster, setRoster] = useState([]);
  const [recent, setRecent] = useState([]);
  const [fairness, setFairness] = useState([]);
  const [members, setMembers] = useState([]);
  const [scoreboard, setScoreboard] = useState([]);
  const [byPerson, setByPerson] = useState([]);
  const [streaks, setStreaks] = useState([]);
  const [sbMonth, setSbMonth] = useState("all");
  const [manageOpen, setManageOpen] = useState(false);
  const [pauseBusy, setPauseBusy] = useState(null);
  const [lightbox, setLightbox] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [note, setNote] = useState("");
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    const [p, r, rc, f, m] = await Promise.all([
      api.get("/linkedin/pending").catch(() => ({ data: { items: [] } })),
      api.get("/linkedin/roster").catch(() => ({ data: { items: [] } })),
      api.get("/linkedin/recent").catch(() => ({ data: [] })),
      api.get("/linkedin/fairness").catch(() => ({ data: { items: [] } })),
      api.get("/linkedin/members").catch(() => ({ data: { items: [] } })),
    ]);
    setItems(p.data?.items || []);
    setTodayAssignee(p.data?.today_assignee || null);
    setRoster(r.data?.items || []);
    setRecent(rc.data || []);
    setFairness(f.data?.items || []);
    setMembers(m.data?.items || []);
    api.get("/linkedin/scoreboard").then(({ data }) => setScoreboard(data?.items || [])).catch(() => setScoreboard([]));
    api.get("/linkedin/streaks").then(({ data }) => setStreaks(data?.items || [])).catch(() => setStreaks([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  // Per-person scoreboard follows the selected month.
  useEffect(() => {
    const url = sbMonth === "all" ? "/linkedin/scoreboard/by-person" : `/linkedin/scoreboard/by-person?month=${sbMonth}`;
    api.get(url).then(({ data }) => setByPerson(data?.items || [])).catch(() => setByPerson([]));
  }, [sbMonth]);

  const exportCsv = () => {
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const lines = ["Month,Approved,Published"];
    scoreboard.forEach((s) => lines.push([esc(MONTH_LABEL(s.month)), s.approved, s.published].join(",")));
    const label = sbMonth === "all" ? "All time" : MONTH_LABEL(sbMonth);
    lines.push("", `Top contributors (${label})`, "Team member,Approved,Published");
    byPerson.forEach((p) => lines.push([esc(p.user_name), p.approved, p.published].join(",")));
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "linkedin-scoreboard.csv";
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const togglePause = async (m) => {
    setPauseBusy(m.user_id);
    try {
      await api.post(m.paused ? "/linkedin/unpause" : "/linkedin/pause", { user_id: m.user_id });
      toast.success(m.paused ? `${m.user_name} is back in the rotation` : `${m.user_name} paused from the rotation`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setPauseBusy(null); }
  };

  const approve = async (row) => {
    setBusyId(row.id);
    try {
      await api.post(`/linkedin/${row.id}/approve`);
      toast.success(`Approved ${row.user_name}'s post`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusyId(null); }
  };

  const requestEdit = async (row) => {
    if (note.trim().length < 3) { toast.error("Add a short note on what to change"); return; }
    setBusyId(row.id);
    try {
      await api.post(`/linkedin/${row.id}/request-edit`, { note });
      toast.success("Sent back for edits");
      setEditingId(null); setNote("");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusyId(null); }
  };

  if (items === null) return null;
  const nothing = items.length === 0 && !todayAssignee && roster.length === 0 && recent.length === 0 && fairness.length === 0 && members.length === 0;
  if (nothing) return null;

  return (
    <div className="px-4 py-3 border-b border-slate-100 bg-[#0A66C2]/5" data-testid="linkedin-approvals">
      <div className="flex items-center gap-2 mb-2">
        <Linkedin size={14} className="text-[#0A66C2]" />
        <span className="text-xs font-semibold text-slate-700">LinkedIn Approvals</span>
        {todayAssignee && (
          <span className="text-[11px] text-slate-500 ml-auto" data-testid="linkedin-today-assignee">
            Today: <b className="text-slate-700">{todayAssignee.user_name}</b> on duty
          </span>
        )}
        <button
          onClick={() => setManageOpen((v) => !v)}
          className={`inline-flex items-center gap-1 text-[11px] rounded-sm px-2 py-1 border transition-colors ${todayAssignee ? "ml-2" : "ml-auto"} ${manageOpen ? "border-[#0A66C2] text-[#0A66C2] bg-white" : "border-slate-200 text-slate-500 hover:text-slate-700"}`}
          data-testid="linkedin-manage-toggle"
        >
          <Users size={12} /> Manage rotation
        </button>
      </div>

      {/* Manage rotation — pause / resume people */}
      {manageOpen && members.length > 0 && (
        <div className="mb-3 rounded-sm border border-slate-200 bg-white p-2.5" data-testid="linkedin-manage">
          <div className="text-[11px] text-slate-500 mb-2">
            Pause someone on long leave or notice period — they'll be skipped until you resume them.
          </div>
          <div className="space-y-1">
            {members.map((m) => (
              <div key={m.user_id} className="flex items-center justify-between gap-2 py-0.5" data-testid={`linkedin-member-${m.user_id}`}>
                <span className={`text-[12px] ${m.paused ? "text-slate-400 line-through" : "text-slate-700"}`}>
                  {m.user_name}{m.paused && <span className="ml-1.5 text-[10px] text-amber-600 no-underline">Paused</span>}
                </span>
                <Button
                  size="sm" variant="outline" onClick={() => togglePause(m)} disabled={pauseBusy === m.user_id}
                  className={`rounded-sm h-6 text-[11px] px-2 ${m.paused ? "border-emerald-300 text-emerald-700 hover:bg-emerald-50" : "border-amber-300 text-amber-700 hover:bg-amber-50"}`}
                  data-testid={`linkedin-pause-toggle-${m.user_id}`}
                >
                  {pauseBusy === m.user_id
                    ? <Loader2 size={11} className="animate-spin" />
                    : (m.paused ? <><Play size={11} className="mr-1" />Resume</> : <><Pause size={11} className="mr-1" />Pause</>)}
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* This week's roster */}
      {roster.length > 0 && (
        <div className="mb-3" data-testid="linkedin-roster">
          <div className="flex items-center gap-1.5 mb-1.5 text-[11px] font-medium text-slate-500">
            <CalendarDays size={12} /> This week's duty roster
          </div>
          <div className="flex flex-wrap gap-1.5">
            {roster.map((r) => (
              <div
                key={r.date}
                className={`rounded-sm border px-2 py-1 text-[11px] ${r.is_today ? "border-[#0A66C2] bg-white text-[#0A66C2] font-medium" : "border-slate-200 bg-white text-slate-600"}`}
                data-testid={`linkedin-roster-${r.date}`}
              >
                <span className="text-slate-400 mr-1">{DOW(r.date)}</span>
                {r.user_name || "— unassigned"}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Pending approvals */}
      {items.length === 0 ? (
        <div className="text-[11px] text-slate-400 italic py-1" data-testid="linkedin-approvals-empty">
          No posts waiting for approval.
        </div>
      ) : (
        <div className="space-y-2">
          {items.map((row) => (
            <div key={row.id} className="rounded-sm border border-slate-200 bg-white p-3" data-testid={`linkedin-approval-${row.id}`}>
              <div className="flex items-center justify-between gap-2 mb-1.5">
                <span className="text-xs font-medium text-slate-700">{row.user_name}</span>
                <span className="text-[10px] text-slate-400">{(row.submitted_at || "").slice(0, 10)}</span>
              </div>
              <div className="flex gap-2">
                {row.image_url && (
                  <img
                    src={row.image_url}
                    alt="attachment"
                    onClick={() => setLightbox(row.image_url)}
                    className="h-16 w-16 rounded-sm object-cover border border-slate-200 shrink-0 cursor-zoom-in hover:opacity-90 transition-opacity"
                    data-testid={`linkedin-approval-image-${row.id}`}
                  />
                )}
                <p className="text-xs text-slate-600 whitespace-pre-wrap" data-testid={`linkedin-approval-content-${row.id}`}>{row.content}</p>
              </div>

              {editingId === row.id ? (
                <div className="space-y-2 mt-2">
                  <Textarea
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    rows={2}
                    placeholder="What should they change?"
                    className="rounded-sm text-xs resize-none"
                    data-testid={`linkedin-edit-note-${row.id}`}
                  />
                  <div className="flex gap-2">
                    <Button size="sm" onClick={() => requestEdit(row)} disabled={busyId === row.id}
                      className="rounded-sm h-7 bg-rose-600 hover:bg-rose-700 text-white" data-testid={`linkedin-send-edit-${row.id}`}>
                      {busyId === row.id ? <Loader2 size={12} className="mr-1 animate-spin" /> : null} Send back
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => { setEditingId(null); setNote(""); }} className="rounded-sm h-7">
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="flex gap-2 mt-2">
                  <Button size="sm" onClick={() => approve(row)} disabled={busyId === row.id}
                    className="rounded-sm h-7 bg-emerald-600 hover:bg-emerald-700 text-white" data-testid={`linkedin-approve-${row.id}`}>
                    {busyId === row.id ? <Loader2 size={12} className="mr-1 animate-spin" /> : <Check size={12} className="mr-1" />} Approve
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => { setEditingId(row.id); setNote(""); }}
                    className="rounded-sm h-7" data-testid={`linkedin-request-edit-${row.id}`}>
                    <MessageSquare size={12} className="mr-1" /> Request Edit
                  </Button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Recently approved — track what went live */}
      {recent.length > 0 && (
        <div className="mt-3" data-testid="linkedin-recent">
          <div className="text-[11px] font-medium text-slate-500 mb-1.5">Recently approved</div>
          <div className="space-y-1">
            {recent.map((r) => (
              <div key={r.id} className="flex items-center justify-between gap-2 rounded-sm bg-white border border-slate-100 px-2.5 py-1.5" data-testid={`linkedin-recent-${r.id}`}>
                <span className="text-[11px] text-slate-600 truncate">
                  <b className="text-slate-700">{r.user_name}</b>
                  <span className="text-slate-400"> · {(r.content || "").slice(0, 48)}{(r.content || "").length > 48 ? "…" : ""}</span>
                </span>
                {r.status === "posted" ? (
                  <span className="shrink-0 inline-flex items-center gap-1 text-[10px] font-medium text-emerald-700" data-testid={`linkedin-recent-posted-${r.id}`}>
                    <CheckCircle2 size={11} /> Posted
                  </span>
                ) : (
                  <span className="shrink-0 inline-flex items-center gap-1 text-[10px] text-amber-600" data-testid={`linkedin-recent-awaiting-${r.id}`}>
                    <Clock size={11} /> Awaiting posting
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
      {/* Fairness — turns taken so far */}
      {fairness.length > 0 && (
        <div className="mt-3" data-testid="linkedin-fairness">
          <div className="flex items-center gap-1.5 mb-1.5 text-[11px] font-medium text-slate-500">
            <BarChart3 size={12} /> Turns taken so far
          </div>
          <div className="flex flex-wrap gap-1.5">
            {fairness.map((f) => (
              <div
                key={f.user_id}
                className="rounded-sm border border-slate-200 bg-white px-2 py-1 text-[11px] text-slate-600"
                data-testid={`linkedin-fairness-${f.user_id}`}
              >
                {f.user_name} <span className="text-slate-400">·</span> <b className="text-slate-700">{f.turns}</b>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Streak badges — consistent publishers */}
      {streaks.length > 0 && (
        <div className="mt-3" data-testid="linkedin-streaks">
          <div className="flex items-center gap-1.5 mb-1.5 text-[11px] font-medium text-slate-500">
            <Flame size={12} /> On a roll
          </div>
          <div className="flex flex-wrap gap-1.5">
            {streaks.map((s) => (
              <div
                key={s.user_id}
                className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[11px] ${STREAK_TIER(s.streak)}`}
                title={`Best run: ${s.best} · ${s.total_published} published in total`}
                data-testid={`linkedin-streak-${s.user_id}`}
              >
                <Flame size={11} />
                <b>{s.user_name}</b>
                <span className="opacity-80">· {s.streak > 0 ? `${s.streak}-turn streak` : `best ${s.best}`}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Monthly scoreboard — approved vs published */}
      {scoreboard.length > 0 && (
        <div className="mt-3" data-testid="linkedin-scoreboard">
          <div className="flex items-center gap-1.5 mb-1.5 text-[11px] font-medium text-slate-500">
            <BarChart3 size={12} /> Monthly scoreboard
            <button
              onClick={exportCsv}
              className="ml-auto inline-flex items-center gap-1 rounded-sm border border-slate-200 px-2 py-0.5 text-slate-500 hover:text-slate-700 hover:border-slate-300 transition-colors"
              data-testid="linkedin-export-csv"
            >
              <Download size={11} /> Download CSV
            </button>
          </div>
          <div className="rounded-sm border border-slate-200 bg-white overflow-hidden">
            <div className="grid grid-cols-3 text-[10px] font-semibold text-slate-400 uppercase tracking-wide px-3 py-1.5 border-b border-slate-100">
              <span>Month</span><span className="text-right">Approved</span><span className="text-right">Published</span>
            </div>
            {scoreboard.map((s) => (
              <div key={s.month} className="grid grid-cols-3 text-[12px] px-3 py-1.5 border-b border-slate-50 last:border-0" data-testid={`linkedin-scoreboard-${s.month}`}>
                <span className="text-slate-700">{MONTH_LABEL(s.month)}</span>
                <span className="text-right text-slate-600">{s.approved}</span>
                <span className="text-right font-medium text-emerald-700">{s.published}</span>
              </div>
            ))}
          </div>

          {/* Top contributors — per person, for the selected month */}
          <div className="mt-3" data-testid="linkedin-by-person">
            <div className="flex items-center gap-1.5 mb-1.5 text-[11px] font-medium text-slate-500">
              <Trophy size={12} /> Top contributors
              <select
                value={sbMonth}
                onChange={(e) => setSbMonth(e.target.value)}
                className="ml-auto rounded-sm border border-slate-200 bg-white text-[11px] px-1.5 py-0.5 text-slate-600"
                data-testid="linkedin-by-person-month"
              >
                <option value="all">All time</option>
                {scoreboard.map((s) => <option key={s.month} value={s.month}>{MONTH_LABEL(s.month)}</option>)}
              </select>
            </div>
            {byPerson.length === 0 ? (
              <div className="text-[11px] text-slate-400 italic">No posts in this period.</div>
            ) : (
              <div className="rounded-sm border border-slate-200 bg-white overflow-hidden">
                <div className="grid grid-cols-[1fr_auto_auto] gap-3 text-[10px] font-semibold text-slate-400 uppercase tracking-wide px-3 py-1.5 border-b border-slate-100">
                  <span>Team member</span><span className="text-right w-16">Approved</span><span className="text-right w-16">Published</span>
                </div>
                {byPerson.map((p, i) => (
                  <div key={p.user_id} className="grid grid-cols-[1fr_auto_auto] gap-3 text-[12px] px-3 py-1.5 border-b border-slate-50 last:border-0" data-testid={`linkedin-by-person-${p.user_id}`}>
                    <span className="text-slate-700 inline-flex items-center gap-1 truncate">
                      {i === 0 && <Trophy size={11} className="text-amber-500 shrink-0" />}{p.user_name}
                    </span>
                    <span className="text-right w-16 text-slate-600">{p.approved}</span>
                    <span className="text-right w-16 font-medium text-emerald-700">{p.published}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Photo lightbox */}
      <Dialog open={!!lightbox} onOpenChange={(v) => !v && setLightbox(null)}>
        <DialogContent className="sm:max-w-2xl p-2" data-testid="linkedin-lightbox">
          {lightbox && <img src={lightbox} alt="full size" className="w-full h-auto rounded-sm" />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
